import { memo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectPlaybackError } from 'store/playbackSlice/playbackSelectors';
import usePlaybackErrorHandling from 'hooks/transaction/playback/usePlaybackErrorHandling';
import useBufferPlaybackVideo from 'hooks/transaction/playback/useBufferPlaybackVideo';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DigitalZoomWraper from '../panels/transaction/cameras/utils/DigitalZoom';

import PropTypes from 'prop-types';
import styles from './PlaybackWindow.module.css';

const PlaybackVideo = ({ url }) => {
    const videoRef = useRef(null);
    const digitalZoomRef = useRef();
    const { bufferedUrl, isBuffering, bufferProgress, bufferError } = useBufferPlaybackVideo(url);
    const { isVideoLoading, videoError } = usePlaybackErrorHandling(videoRef, bufferedUrl);
    const error = useSelector(selectPlaybackError);

    if (error) {
        return (
            <div className={styles['playback-video--error']}>
                <span>{error}</span>
            </div>
        );
    }

    if (!url) {
        return (
            <div className={styles['playback-video--placeholder']}>
                <span>no footage available</span>
            </div>
        );
    }

    if (bufferError) {
        return (
            <div className={styles['playback-video--error']}>
                <span>{bufferError}</span>
            </div>
        );
    }

    if (videoError) {
        return (
            <div className={styles['playback-video--error']}>
                <span>{videoError}</span>
            </div>
        );
    }

    if (isBuffering) {
        return (
            <div className={styles['playback-video--loading']}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress variant="determinate" value={bufferProgress} size={50} sx={{ color: '#fff' }} />
                    <Box
                        sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="caption" component="div" sx={{ color: '#fff', fontSize: '0.65rem' }}>
                            {`${bufferProgress}%`}
                        </Typography>
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1.5, fontSize: '0.75rem' }}>
                    Buffering video...
                </Typography>
            </div>
        );
    }

    return (
        <>
            {isVideoLoading && (
                <div className={styles['playback-video--loading']}>
                    <CircularProgress size={40} sx={{ color: '#fff' }} />
                </div>
            )}
            <DigitalZoomWraper
                config={{
                    limitToBounds: true,
                    ref: digitalZoomRef,
                }}
            >
                <video
                    ref={videoRef}
                    className={styles['playback-video']}
                    src={bufferedUrl}
                    controls
                    autoPlay
                    playsInline
                    muted
                    crossOrigin="anonymous"
                    style={{ display: isVideoLoading ? 'none' : 'block' }}
                />
            </DigitalZoomWraper>
        </>
    );
};

PlaybackVideo.propTypes = {
    url: PropTypes.string,
};

export default memo(PlaybackVideo);
